import type { QueryClient } from "@tanstack/react-query";
import type { KpiListFilters } from "../api/types.js";

/**
 * TanStack Query key factory for the KPI Manager. One `["kpi", …]` namespace so
 * a mutation can invalidate every list variant at the `lists()` prefix
 * regardless of the active filters, and a single detail by id.
 */
export const kpiKeys = {
  all: ["kpi"] as const,
  lists: () => [...kpiKeys.all, "list"] as const,
  list: (filters: KpiListFilters) => [...kpiKeys.lists(), filters] as const,
  details: () => [...kpiKeys.all, "detail"] as const,
  detail: (kpiId: string) => [...kpiKeys.details(), kpiId] as const,
  definitions: () => [...kpiKeys.all, "definitions"] as const,
  definitionPage: (cursor: string | undefined, limit: number) =>
    [...kpiKeys.definitions(), "page", cursor ?? "first", limit] as const,
  /** A single KPI Type resolved by id — used to dereference `compatibility` /
   * `deprecated` Types the browsable catalog list hides. */
  definition: (definitionId: string) =>
    [...kpiKeys.definitions(), definitionId] as const,
  /** Owner id → name resolution, inside the `kpi` namespace. `scopeKey` is
   * the host auth scope (see {@link kpiKeys.ownerOptionIds}): resolved names
   * are identity data, so one tenant/session's entries must never be served
   * to the next across an in-page auth transition. */
  ownerNames: (scopeKey: string, ids: readonly string[]) =>
    [...kpiKeys.all, "owner-names", scopeKey, ids] as const,
  /** The complete distinct owner-id set behind the Owner filter (PB-2941) —
   * ONE cache entry per auth scope (the client sweeps the cursor), deduped by
   * TanStack. `scopeKey` is the host auth scope (tenant/actor/generation, the
   * SOLVE/Hoshin scope-keyed-cache pattern): the shell's QueryClient survives
   * in-page auth transitions, so a scope-blind key could serve one tenant's
   * owner ids to the next. */
  ownerOptionIds: (scopeKey: string) =>
    [...kpiKeys.all, "owner-option-ids", scopeKey] as const,
  /**
   * PB-2941 — the complete Category option set behind the Create page. ONE
   * cache entry (the hook sweeps the cursor), so the picker re-renders from
   * cache instead of refetching. Deliberately NOT scope-keyed, unlike
   * `ownerOptionIds`: category codes are business data like the KPI lists and
   * `definitions`, not identity data, so they follow that convention rather
   * than the identity-cache one.
   */
  categoryOptions: () => [...kpiKeys.all, "category-options"] as const,
  /**
   * PB-2941 — one KPI's observation history for ONE range. `rangeKey` is the
   * chosen window (`30d` / `90d` / `all`) rather than the resolved `from`
   * instant: the instant moves every millisecond, which would make every render
   * a cache miss and refetch the whole series. The hook re-resolves the instant
   * when the query actually runs.
   *
   * Nested under `observations()` so recording a value can invalidate every
   * range of one KPI at a prefix — the new reading belongs in all of them.
   */
  observations: (kpiId: string) =>
    [...kpiKeys.all, "observations", kpiId] as const,
  observationRange: (kpiId: string, rangeKey: string) =>
    [...kpiKeys.observations(kpiId), rangeKey] as const,
};

/**
 * What every observation WRITE staleifies, single and batch alike — stated
 * once so the two mutation hooks cannot drift. Three invalidations, each for
 * its own reason:
 *  - the KPI's DETAIL, because the write re-points `currentState` (and a
 *    backdated entry may legitimately leave it unchanged — only a refetch can
 *    say which);
 *  - EVERY range of this KPI's observations, at the `observations(kpiId)`
 *    prefix, because new rows belong in whichever windows contain them;
 *  - the LISTS, because `currentState` and `readiness` ride on the summary row
 *    since PB-98, so a card in the collection behind the page is now stale.
 */
export function invalidateAfterObservationWrite(
  qc: QueryClient,
  kpiId: string,
): void {
  void qc.invalidateQueries({ queryKey: kpiKeys.detail(kpiId) });
  void qc.invalidateQueries({ queryKey: kpiKeys.observations(kpiId) });
  void qc.invalidateQueries({ queryKey: kpiKeys.lists() });
}
